import { useEffect } from "react";
import { WidgetCommunicationService } from "@/services/integrations/widget/WidgetCommunicationService";

/**
 * Side-effect-only embed-mode chrome: notifies the parent widget popup when
 * ready and when the conversation starts, hides the public Help Genie widget
 * bubble while this page is mounted (unconditionally — it overlaps the voice
 * UI on every surface, not just embed), and suppresses iframe document
 * scrollbars in embed mode.
 */
export function useEmbedChrome(
  isEmbedMode: boolean,
  urlName: string | undefined,
  voiceConnected: boolean,
  elevenlabsAgentId: string | undefined,
) {
  // Embed mode communication - notify parent widget when ready
  useEffect(() => {
    if (isEmbedMode) {
      WidgetCommunicationService.sendToParent("helpgenie:ready", { agentUrl: urlName });
    }
  }, [isEmbedMode, urlName]);

  // Notify parent when conversation state changes (for embed mode)
  useEffect(() => {
    if (isEmbedMode && voiceConnected) {
      WidgetCommunicationService.sendToParent("helpgenie:conversation:started", {
        agentId: elevenlabsAgentId,
      });
    }
  }, [isEmbedMode, voiceConnected, elevenlabsAgentId]);

  // Hide the public Help Genie widget bubble while on this page — it overlaps
  // the voice UI and doesn't belong on agent-facing surfaces.
  useEffect(() => {
    const v1 = document.getElementById("helpgenie-widget-container");
    const v2 = document.querySelector<HTMLElement>(".hgv2-host");
    if (v1) v1.style.display = "none";
    if (v2) v2.style.display = "none";
    return () => {
      if (v1) v1.style.display = "";
      if (v2) v2.style.display = "";
    };
  }, []);

  // Suppress iframe document scrollbars in embed mode. The widget popup has its
  // own fixed dimensions; any overflow inside should be clipped, not scrolled.
  useEffect(() => {
    if (!isEmbedMode) return;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [isEmbedMode]);
}
